// Static site content data

export const HERO_SLIDES = [
  {
    id: 'seaport',
    kicker: 'Seaports',
    title: 'Patented Computer Vision Yard AI',
    titleAccent: ' for Seaport Operations',
    subtitle: 'Real-time berth, crane, and gate intelligence — from vessel arrival to yard hand-off.',
    video: 'https://videos.pexels.com/video-files/2932301/2932301-uhd_2560_1440_24fps.mp4',
    poster: 'https://images.pexels.com/photos/28185415/pexels-photo-28185415.jpeg',
    metrics: [
      { label: 'Vessels tracked', value: '128' },
      { label: 'Detection accuracy', value: '99.2%' },
      { label: 'Dwell reduction', value: '-31%' },
    ],
  },
  {
    id: 'warehouse',
    kicker: 'Warehouse',
    title: 'VLM AI',
    titleAccent: ' for Warehouse Automation',
    subtitle: 'Detect, count, and orchestrate — forklifts, pickers, pallets, and safety zones on one live graph.',
    video: 'https://videos.pexels.com/video-files/4487006/4487006-uhd_2560_1440_25fps.mp4',
    poster: 'https://images.pexels.com/photos/34207359/pexels-photo-34207359.jpeg',
    metrics: [
      { label: 'Throughput uplift', value: '+22%' },
      { label: 'Safety incidents', value: '-64%' },
      { label: 'Live cameras', value: '2,400' },
    ],
  },
  {
    id: 'airport',
    kicker: 'Airports',
    title: 'Computer Vision AI',
    titleAccent: ' for Airside & Cargo Operations',
    subtitle: 'Ramp, apron, and cargo intelligence — reduce turnaround, prevent FOD, keep flights on time.',
    video: 'https://videos.pexels.com/video-files/2231485/2231485-hd_1920_1080_30fps.mp4',
    poster: 'https://images.pexels.com/photos/27490332/pexels-photo-27490332.jpeg',
    metrics: [
      { label: 'On-time turnaround', value: '+18%' },
      { label: 'FOD events auto-flagged', value: '1.4k / mo' },
      { label: 'Gates covered', value: '96' },
    ],
  },
  {
    id: 'container',
    kicker: 'Container Damage',
    title: 'Container Damage Detection',
    titleAccent: ' at Line-Speed',
    subtitle: 'Six-side inspection with sub-millimeter defect classification and auto EIR generation.',
    video: 'https://videos.pexels.com/video-files/4489743/4489743-uhd_2560_1440_25fps.mp4',
    poster: 'https://images.pexels.com/photos/28438249/pexels-photo-28438249.jpeg',
    metrics: [
      { label: 'Inspection speed', value: '8 sec / TEU' },
      { label: 'Defect classes', value: '42' },
      { label: 'False-positive rate', value: '<0.6%' },
    ],
  },
];

export const PLATFORMS = [
  {
    id: 'industrial-ai',
    name: 'Industrial AI Platform',
    tag: 'Core',
    description: 'The operational core of Visotonics — unified telemetry, live context graph, and vision agents that reason across your physical operations.',
    features: ['Unified video pipeline', 'Edge + cloud inference', 'Context Graph', 'Policy Engine'],
  },
  {
    id: 'visopercept',
    name: 'VisoPercept',
    tag: 'Perception',
    description: 'Multi-camera perception engine for objects, actions, anomalies. Pre-trained industrial classes with fine-tuning in minutes.',
    features: ['200+ industrial classes', 'Zero-shot fine-tune', 'Multi-cam fusion', 'Auto-labeling'],
  },
  {
    id: 'digital-twin',
    name: 'Digital Twin',
    tag: 'Simulation',
    description: 'Live twin of your seaport, warehouse or airfield — replay incidents, simulate throughput, plan for capacity.',
    features: ['Live 2D/3D twin', 'Incident replay', 'What-if simulation', 'Capacity planning'],
  },
];

export const INDUSTRIES = [
  {
    id: 'seaport',
    name: 'Seaport',
    tag: 'Terminals · Berths · Gates',
    image: 'https://images.pexels.com/photos/14068258/pexels-photo-14068258.jpeg',
    outcomes: ['-31% vessel dwell', '+22% crane productivity', 'Auto EIR at gate'],
  },
  {
    id: 'warehouse',
    name: 'Warehouse',
    tag: 'DCs · 3PL · Cold Chain',
    image: 'https://images.pexels.com/photos/36522028/pexels-photo-36522028.jpeg',
    outcomes: ['-64% safety events', '+22% throughput', '99.8% pick verification'],
  },
  {
    id: 'airport',
    name: 'Airport',
    tag: 'Airside · Ramp · Cargo',
    image: 'https://images.pexels.com/photos/22589659/pexels-photo-22589659.jpeg',
    outcomes: ['+18% on-time turnaround', 'FOD auto-detection', 'Ramp safety compliance'],
  },
  {
    id: 'enterprise',
    name: 'Enterprise',
    tag: 'Cross-industry · Multi-site',
    image: 'https://images.unsplash.com/photo-1767112601499-1b78cfb31a62',
    outcomes: ['Unified vision fabric', 'Multi-site rollout', 'ROI in <90 days'],
  },
];

export const SERVICES = [
  { stage: '01', title: 'Assess', items: ['Site survey', 'Camera & edge audit', 'Use-case ROI model'] },
  { stage: '02', title: 'Design', items: ['Architecture blueprint', 'Model catalog', 'Policy & alerts'] },
  { stage: '03', title: 'Build', items: ['Pipeline build', 'Model fine-tune', 'Twin authoring'] },
  { stage: '04', title: 'Integrate', items: ['TOS · WMS · AODB', 'ERP · gate systems', 'BI + data lake'] },
  { stage: '05', title: 'Deploy', items: ['Edge rollout', 'Multi-site scale', 'SRE handover'] },
  { stage: '06', title: 'Operate', items: ['24x7 MDR for vision', 'Model drift care', 'SLA management'] },
  { stage: '07', title: 'Optimize', items: ['Continuous tuning', 'New class onboarding', 'Twin what-ifs'] },
  { stage: '08', title: 'Transform', items: ['Autonomous ops', 'Cross-site playbooks', 'Value scale-up'] },
];

export const STATS = [
  { value: '99.2%', label: 'Detection accuracy across industrial classes' },
  { value: '85+', label: 'Ports, DCs and airfields on Visotonics' },
  { value: '-31%', label: 'Average dwell / turnaround reduction' },
  { value: '<90d', label: 'Median time-to-first-value' },
];
